/* MainVisualSliderSync だけを sync contract に登録する */
(() => {
  /*
   * script の評価順は保証されないため、main visual 本体より先に評価された場合は
   * pending queue へ積み、本体側の初期化時にまとめて register してもらう。
   */
  function registerMainVisualSyncMembers(syncMembers) {
    const registerSyncMembers = window.AuremeMainVisual?.registerSyncMembers;
    if (typeof registerSyncMembers === 'function') return registerSyncMembers(syncMembers);

    window.AuremeMainVisual = window.AuremeMainVisual || {};
    window.AuremeMainVisual.pendingSyncMembers = window.AuremeMainVisual.pendingSyncMembers || [];
    window.AuremeMainVisual.pendingSyncMembers.push(syncMembers);

    return null;
  }

  const Sync = window.AuremeMainVisual?.Sync || {};

  if (Sync.MainVisualSliderSync) {
    /* sync.js だけ再評価された場合も、ready 判定済みなら main visual.js との ready event 契約を満たす */
    Sync.notifyReadyIfReady?.(Sync);
    return;
  }

  const DEFAULT_SLIDER_DELAY = 5000;
  const DEFAULT_SLIDER_EFFECT = 'fade';
  const DESKTOP_VIEWPORT_NAME = 'desktop';
  const MOBILE_VIEWPORT_NAME = 'mobile';
  const LOOP_CLONE_ATTRIBUTE = 'data-aureme-main-visual-loop-clone';
  const LOOP_CLONE_RESET_BUFFER = 20;
  const ACTIVE_MEDIA_SELECTOR = '.swiper-slide-active .aureme-main-visual__media-element';
  const MEDIA_ELEMENT_SELECTOR = '.aureme-main-visual__media-element';
  const ORIGINAL_SLIDE_SELECTOR = `.swiper-slide:not([${LOOP_CLONE_ATTRIBUTE}])`;
  const NO_MEDIA_CLASS_BY_VIEWPORT = {
    [DESKTOP_VIEWPORT_NAME]: 'aureme-main-visual--no-desktop-media',
    [MOBILE_VIEWPORT_NAME]: 'aureme-main-visual--no-mobile-media',
  };
  const SUPPORTED_SLIDER_EFFECTS = new Set([
    'slide',
    'fade',
    'cube',
    'coverflow',
    'flip',
    'creative',
    'cards',
  ]);
  const SLIDER_TRANSITION_SPEED = 800;
  const SWIPER_READY_RETRY_DELAY = 50;
  const SWIPER_READY_RETRY_LIMIT = 100;
  const createReadyRetryScheduler = window.AuremeReadyRetry?.createScheduler;

  function getActiveMainVisualViewportName(breakpointQuery) {
    return breakpointQuery.matches ? DESKTOP_VIEWPORT_NAME : MOBILE_VIEWPORT_NAME;
  }

  function getActiveMainVisualViewport(element, breakpointQuery) {
    const viewportName = getActiveMainVisualViewportName(breakpointQuery);
    return element.querySelector(`.aureme-main-visual__viewport--${viewportName}`);
  }

  function getActiveOrFirstMainVisualMediaElement(container) {
    if (!container) return null;

    return (
      container.querySelector(ACTIVE_MEDIA_SELECTOR) ||
      container.querySelector(MEDIA_ELEMENT_SELECTOR)
    );
  }

  /* 表示中スライドの画像は LCP 候補のため、遅延読み込みを外して優先取得させる */
  function prioritizeMainVisualImage(image) {
    if (image?.tagName !== 'IMG') return;

    image.setAttribute('loading', 'eager');
    image.setAttribute('fetchpriority', 'high');
  }

  function normalizeMainVisualSliderEffect(effect) {
    if (SUPPORTED_SLIDER_EFFECTS.has(effect)) return effect;

    return DEFAULT_SLIDER_EFFECT;
  }

  function normalizeMainVisualAutoplayDelay(delay) {
    const autoplayDelay = Number.parseInt(delay, 10);
    if (Number.isFinite(autoplayDelay) && autoplayDelay > 0) return autoplayDelay;

    return DEFAULT_SLIDER_DELAY;
  }

  function getMainVisualSliderEffectOptions(effect) {
    const effectOptions = {
      effect,
    };

    if (effect === 'fade') {
      effectOptions.fadeEffect = {
        crossFade: true,
      };
    } else if (effect === 'creative') {
      effectOptions.creativeEffect = {
        prev: {
          translate: [0, 0, -400],
        },
        next: {
          translate: ['100%', 0, 0],
        },
      };
    }

    return effectOptions;
  }

  class MainVisualLoopCloneReset {
    constructor({ prioritizeActiveImage, slideTo }) {
      this.prioritizeActiveImage = prioritizeActiveImage;
      this.slideTo = slideTo;
      this.timeouts = new Map();
    }

    disconnect() {
      for (const timeout of this.timeouts.values()) {
        window.clearTimeout(timeout);
      }

      this.timeouts.clear();
    }

    schedule(instance, slider) {
      this.cancel(instance);
      if (!this.isLoopCloneActive(instance)) return;

      /* virtualTranslate 系 effect は transitionEnd が発火しない場合があるため、speed 後にも戻す */
      const timeout = window.setTimeout(() => {
        this.reset(instance, slider);
      }, instance.params.speed + LOOP_CLONE_RESET_BUFFER);

      this.timeouts.set(instance, timeout);
    }

    cancel(instance) {
      const timeout = this.timeouts.get(instance);
      if (!timeout) return;

      window.clearTimeout(timeout);
      this.timeouts.delete(instance);
    }

    reset(instance, slider) {
      this.cancel(instance);
      if (!this.isLoopCloneActive(instance)) return;

      const shouldRestartAutoplay = instance.autoplay?.running;

      /* 先頭クローンへの通常遷移後に、同じ画像の本来の先頭へ無音で戻す */
      this.slideTo(instance, 0, 0);
      if (shouldRestartAutoplay) {
        instance.autoplay.stop();
        instance.autoplay.start();
      }
      this.prioritizeActiveImage(slider);
    }

    isLoopCloneActive(instance) {
      return instance.slides[instance.activeIndex]?.hasAttribute(LOOP_CLONE_ATTRIBUTE) || false;
    }
  }

  /*
   * desktop / mobile の active viewport だけ Swiper を動かし、非表示側は先頭へ戻して停止する。
   * Swiper 読み込み順が遅れた場合も、ready retry で初回 sync を取りこぼさない。
   */
  class MainVisualSliderSync {
    constructor(element, breakpointQuery, autoplayDelay, slideEffect) {
      this.element = element;
      this.breakpointQuery = breakpointQuery;
      this.autoplayDelay = autoplayDelay;
      this.slideEffect = slideEffect;
      this.instances = new Map();
      this.loopCloneReset = new MainVisualLoopCloneReset({
        prioritizeActiveImage: (slider) => this.prioritizeActiveImage(slider),
        slideTo: (instance, index, speed) => this.slideTo(instance, index, speed),
      });
      this.swiperReadyRetryScheduler =
        typeof createReadyRetryScheduler === 'function'
          ? createReadyRetryScheduler({
              intervalMs: SWIPER_READY_RETRY_DELAY,
              maxRetryCount: SWIPER_READY_RETRY_LIMIT,
            })
          : null;
      this.swiperReadyRetryCount = 0;
    }

    sync() {
      const activeViewport = this.getActiveViewport();

      for (const slider of this.getSliders()) {
        if (activeViewport?.contains(slider)) {
          this.activateSlider(slider);
        } else {
          this.deactivateSlider(slider);
        }
      }
    }

    disconnect() {
      this.cancelSwiperReadyRetry();

      for (const instance of this.instances.values()) {
        instance.destroy(true, true);
      }

      this.loopCloneReset.disconnect();
      this.instances.clear();
    }

    getSliders() {
      return this.element.querySelectorAll('[data-aureme-main-visual-slider]');
    }

    getActiveViewport() {
      return getActiveMainVisualViewport(this.element, this.breakpointQuery);
    }

    activateSlider(slider) {
      const instance = this.getOrCreateSlider(slider);
      if (!instance?.autoplay || instance.autoplay.running) return;

      instance.autoplay.start();
    }

    deactivateSlider(slider) {
      const instance = this.instances.get(slider);
      if (!instance) return;

      if (instance.autoplay?.running) {
        instance.autoplay.stop();
      }

      this.loopCloneReset.cancel(instance);

      /* viewport を跨いで戻った時は先頭画像から再開し、切替途中の状態を残さない */
      this.slideTo(instance, 0, 0);
    }

    getOrCreateSlider(slider) {
      if (this.instances.has(slider)) {
        return this.instances.get(slider);
      }

      if (!window.Swiper) {
        this.scheduleSwiperReadyRetry();
        return null;
      }

      this.cancelSwiperReadyRetry();

      if (this.getOriginalSlides(slider).length <= 1) {
        return null;
      }

      const instance = new window.Swiper(slider, {
        allowTouchMove: false,
        autoplay: {
          delay: this.autoplayDelay,
          disableOnInteraction: false,
        },
        ...getMainVisualSliderEffectOptions(this.slideEffect),
        slidesPerView: 1,
        speed: SLIDER_TRANSITION_SPEED,
        on: {
          init: () => this.prioritizeActiveImage(slider),
          slideChangeTransitionEnd: (swiper) => this.loopCloneReset.reset(swiper, slider),
          slideChangeTransitionStart: (swiper) => {
            this.prioritizeActiveImage(slider);
            this.loopCloneReset.schedule(swiper, slider);
          },
        },
      });

      this.instances.set(slider, instance);
      this.prioritizeActiveImage(slider);

      return instance;
    }

    selectBlock(blockId) {
      const slider = this.getActiveViewport()?.querySelector('[data-aureme-main-visual-slider]');
      if (!slider) return;

      const instance = this.getOrCreateSlider(slider);
      if (!instance) return;

      const slideIndexAttribute = this.getOriginalSlides(slider).find(
        (slide) => slide.dataset.auremeMainVisualBlockId === blockId,
      )?.dataset.auremeMainVisualSlideIndex;
      const slideIndex = Number.parseInt(slideIndexAttribute, 10);
      if (!Number.isFinite(slideIndex)) return;

      instance.autoplay?.stop();
      this.slideTo(instance, slideIndex, 0);
      this.prioritizeActiveImage(slider);
    }

    resumeActiveSlider() {
      this.sync();
    }

    getOriginalSlides(slider) {
      return Array.from(slider.querySelectorAll(ORIGINAL_SLIDE_SELECTOR));
    }

    slideTo(instance, index, speed) {
      instance.slideTo(index, speed, false);
    }

    prioritizeActiveImage(slider) {
      prioritizeMainVisualImage(getActiveOrFirstMainVisualMediaElement(slider));
    }

    scheduleSwiperReadyRetry() {
      if (this.swiperReadyRetryCount >= SWIPER_READY_RETRY_LIMIT) return;

      this.swiperReadyRetryScheduler?.schedule({
        tryReady: () => Boolean(window.Swiper),
        onRetry: (nextRetryCount) => {
          this.swiperReadyRetryCount = nextRetryCount;
          this.sync();
        },
        retryCount: this.swiperReadyRetryCount,
      });
    }

    cancelSwiperReadyRetry() {
      this.swiperReadyRetryScheduler?.clear();
      this.swiperReadyRetryCount = 0;
    }
  }

  registerMainVisualSyncMembers({
    MainVisualSliderSync,
    /* constants/helpers は分割 controller が参照する内部契約 */
    constants: {
      DESKTOP_VIEWPORT_NAME,
      MOBILE_VIEWPORT_NAME,
      NO_MEDIA_CLASS_BY_VIEWPORT,
    },
    helpers: {
      getActiveMainVisualViewport,
      getActiveMainVisualViewportName,
      getActiveOrFirstMainVisualMediaElement,
      prioritizeMainVisualImage,
    },
    normalizeMainVisualAutoplayDelay,
    normalizeMainVisualSliderEffect,
  });
})();
